// The Dashboard, Requests, Revenue and Trending tabs' side of the server's
// stats API (server lib/admin/stats.js), plus the All Payouts and Disputes
// totals (GET /payouts/summary, /disputes/summary): the request URLs, and
// parsers that turn an answer into the shape each tab draws. A missing or
// malformed field reads as empty / zero, so a bad answer shows an empty tab,
// never a crash. No React, no network of their own.
import type {
  AdminDisputeSummary,
  AdminMoney,
  AdminOverviewStats,
  AdminPayoutSummary,
  AdminRequestsPage,
  AdminRevenueStats,
  AdminStatusMix,
  AdminTrendingStats,
  TrendingRanks,
} from "../../types/admin";

type Loose = Record<string, unknown>;
const obj = (v: unknown): Loose => (v && typeof v === "object" && !Array.isArray(v) ? (v as Loose) : {});
const num = (v: unknown): number => (typeof v === "number" && Number.isFinite(v) ? v : 0);
const list = <T>(v: unknown): T[] => (Array.isArray(v) ? (v as T[]) : []);
const money = (v: unknown): AdminMoney => {
  const out: AdminMoney = {};
  for (const [k, x] of Object.entries(obj(v))) if (typeof x === "number") out[k] = x;
  return out;
};
const sinceOf = (r: Loose) => (typeof r.since === "string" ? r.since : null);

// The window's first instant, or none for All Time.
export function statsUrl(tab: "overview" | "revenue" | "trending", since: number | Date | null): string {
  if (since === null) return `/stats/${tab}`;
  return `/stats/${tab}?since=${encodeURIComponent(new Date(since).toISOString())}`;
}

export interface RequestsQuery {
  status: string;
  dj: string;
  q: string;
  from: Date | null;
  to: Date | null;
  missingTrackHistory: boolean;
  sort: "date" | "amount";
}

// One page of the Requests table: the 500 the table draws.
export const REQUESTS_PAGE = 500;

export function requestsUrl(f: RequestsQuery): string {
  const params = new URLSearchParams();
  if (f.status !== "all") params.set("status", f.status);
  if (f.dj !== "all") params.set("dj", f.dj);
  if (f.q.trim()) params.set("q", f.q.trim());
  if (f.from) params.set("from", f.from.toISOString());
  if (f.to) params.set("to", f.to.toISOString());
  if (f.missingTrackHistory) params.set("missingTrackHistory", "1");
  if (f.sort !== "date") params.set("sort", f.sort);
  params.set("limit", String(REQUESTS_PAGE));
  return `/requests?${params.toString()}`;
}

const MIX: (keyof AdminStatusMix)[] = ["selected", "pending", "queued", "accepted", "declined", "played"];

export function overviewFrom(raw: unknown): AdminOverviewStats {
  const r = obj(raw);
  const k = obj(r.kpis);
  const mix = obj(r.statusMix);
  return {
    since: sinceOf(r),
    kpis: {
      earningsByCurrency: money(k.earningsByCurrency),
      volumeByCurrency: money(k.volumeByCurrency),
      talentPayoutsByCurrency: money(k.talentPayoutsByCurrency),
      paidCount: num(k.paidCount),
      undatedPaidCount: num(k.undatedPaidCount),
      orderCount: num(k.orderCount),
    },
    statusMix: Object.fromEntries(MIX.map((s) => [s, num(mix[s])])) as AdminStatusMix,
    topTalent: list<Loose>(r.topTalent).map((d) => ({
      id: typeof d.id === "string" ? d.id : null,
      name: String(d.name ?? ""),
      avatarUrl: typeof d.avatarUrl === "string" ? d.avatarUrl : null,
      barPct: num(d.barPct),
      amountByCurrency: money(d.amountByCurrency),
      count: num(d.count),
    })),
  };
}

export function revenueFrom(raw: unknown): AdminRevenueStats {
  const r = obj(raw);
  return {
    since: sinceOf(r),
    byCurrency: list<Loose>(r.byCurrency).map((c) => ({
      currency: String(c.currency ?? ""),
      volume: num(c.volume),
      fees: num(c.fees),
      talentShare: num(c.talentShare),
      count: num(c.count),
    })),
    undatedPaidCount: num(r.undatedPaidCount),
    topTalent: list<Loose>(r.topTalent).map((d) => ({
      id: typeof d.id === "string" ? d.id : null,
      name: String(d.name ?? ""),
      avatarUrl: typeof d.avatarUrl === "string" ? d.avatarUrl : null,
      count: num(d.count),
      revenueByCurrency: money(d.revenueByCurrency),
      feeByCurrency: money(d.feeByCurrency),
      avgByCurrency: money(d.avgByCurrency),
    })),
  };
}

const ranksFrom = (v: unknown, size: number): TrendingRanks => {
  const r = obj(v);
  const pick = (s: string, d: string) => list<unknown>(obj(r[s])[d]).filter((i): i is number => Number.isInteger(i) && (i as number) >= 0 && (i as number) < size);
  const sorts = ["total", "paid", "selected", "revenue"] as const;
  return Object.fromEntries(sorts.map((s) => [s, { desc: pick(s, "desc"), asc: pick(s, "asc") }])) as TrendingRanks;
};

export function trendingFrom(raw: unknown): AdminTrendingStats {
  const r = obj(raw);
  const songs = list<Loose>(r.songs).map((s) => ({
    query: String(s.query ?? ""),
    artists: list<unknown>(s.artists).map(String),
    image: typeof s.image === "string" ? s.image : null,
    paidCount: num(s.paidCount),
    selectedCount: num(s.selectedCount),
    revenueByCurrency: money(s.revenueByCurrency),
  }));
  const artists = list<Loose>(r.artists).map((a) => ({
    name: String(a.name ?? ""),
    paidCount: num(a.paidCount),
    selectedCount: num(a.selectedCount),
    revenueByCurrency: money(a.revenueByCurrency),
  }));
  return {
    since: sinceOf(r),
    orderCount: num(r.orderCount),
    songs,
    songRanks: ranksFrom(r.songRanks, songs.length),
    artists,
    artistRanks: ranksFrom(r.artistRanks, artists.length),
  };
}

export function requestsFrom(raw: unknown): AdminRequestsPage {
  const r = obj(raw);
  const k = obj(r.kpis);
  return {
    items: list(r.items),
    total: num(r.total),
    kpis: {
      grossByCurrency: money(k.grossByCurrency),
      feesByCurrency: money(k.feesByCurrency),
      avgByCurrency: money(k.avgByCurrency),
      completed: num(k.completed),
    },
  };
}

// All Payouts' KPI cards: every payout from the window's first instant (none
// for All Time), the same instant the tab's table filters on.
export function payoutSummaryUrl(since: number | null): string {
  if (since === null) return "/payouts/summary";
  return `/payouts/summary?since=${encodeURIComponent(new Date(since).toISOString())}`;
}

export function payoutSummaryFrom(raw: unknown): AdminPayoutSummary {
  const r = obj(raw);
  return {
    count: num(r.count),
    grossByCurrency: money(r.grossByCurrency),
    netByCurrency: money(r.netByCurrency),
    undatedCount: num(r.undatedCount),
  };
}

export const DISPUTE_SUMMARY_URL = "/disputes/summary";

// Currency codes folded to lower case, the way the Disputes tab has always
// added them up.
export function disputeSummaryFrom(raw: unknown): AdminDisputeSummary {
  const r = obj(raw);
  const amountByCurrency: AdminMoney = {};
  for (const [cur, cents] of Object.entries(money(r.amountByCurrency))) {
    const key = (cur || "usd").toLowerCase();
    amountByCurrency[key] = (amountByCurrency[key] || 0) + cents;
  }
  return {
    total: num(r.total),
    needsResponse: num(r.needsResponse),
    underReview: num(r.underReview),
    won: num(r.won),
    lost: num(r.lost),
    amountByCurrency,
  };
}
